import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { BotAnalysisConfig, ParticipantT } from '@/types/meeting';

import { createStoreWithSelectors } from '@/utils/store';

export interface MeetingStore {
  readonly meetingLink: string;
  readonly meetingId: string;
  readonly participants: ParticipantT[];
  readonly consentSentByBotId: Record<string, string[]>;
  readonly botAnalysisConfigs: Record<string, BotAnalysisConfig>;
  actions: {
    setMeetingLink: (link: string) => void;
    setMeetingId: (id: string) => void;
    setParticipants: (participants: ParticipantT[]) => void;
    clearParticipants: () => void;
    addConsentSent: (botId: string, participantIds: string[]) => void;
    clearAllConsentSent: () => void;
    clearConsentSentForBot: (botId: string) => void;
    setBotAnalysisConfig: (botId: string, config: BotAnalysisConfig) => void;
    clearBotAnalysisConfig: (botId: string) => void;
    clearBotAnalysisConfigs: () => void;
  };
}

export const meetingStore = create<MeetingStore>()(
  persist(
    (setState) => ({
      meetingLink: '',
      meetingId: '',
      participants: [],
      consentSentByBotId: {},
      botAnalysisConfigs: {},
      actions: {
        setMeetingLink: (meetingLink) => setState({ meetingLink }),
        setMeetingId: (meetingId) => setState({ meetingId }),
        setParticipants: (participants) => setState({ participants }),
        clearParticipants: () => setState({ participants: [] }),
        addConsentSent: (botId, participantIds) =>
          setState((state) => ({
            consentSentByBotId: {
              ...state.consentSentByBotId,
              [botId]: [
                ...(state.consentSentByBotId[botId] ?? []),
                ...participantIds,
              ],
            },
          })),
        clearAllConsentSent: () => setState({ consentSentByBotId: {} }),
        clearConsentSentForBot: (botId) =>
          setState((state) => {
            const consentSentByBotId = { ...state.consentSentByBotId };
            delete consentSentByBotId[botId];
            return { consentSentByBotId };
          }),
        clearBotAnalysisConfigs: () => setState({ botAnalysisConfigs: {} }),
        clearBotAnalysisConfig: (botId) =>
          setState((state) => {
            const configs = { ...state.botAnalysisConfigs };
            delete configs[botId];
            return { botAnalysisConfigs: configs };
          }),
        setBotAnalysisConfig: (botId, config) =>
          setState((state) => ({
            botAnalysisConfigs: {
              ...state.botAnalysisConfigs,
              [botId]: config,
            },
          })),
      },
    }),
    {
      name: 'meeting-store',
      partialize: (state) => ({
        consentSentByBotId: state.consentSentByBotId,
        botAnalysisConfigs: state.botAnalysisConfigs,
      }),
    },
  ),
);

export const useMeetingState = createStoreWithSelectors(meetingStore);
